/**
 * Pull requests as part of the work (mju-ai-workbench docs/plans/2026-09-project-model-end-to-end.md,
 * R8, the organisation's half). AIOE keeps one row per pull request of a project, fed by the
 * workbench's monitor and by provider webhooks; files the review-requested, check-failed and
 * merged records for members whose workbench is off; and answers an approval, a request for
 * changes, a comment or a merge through the person's own linked account.
 *
 *   POST   /projects/v1/:id/pull-requests                upsert by provider + repository + number → the row (201 new, 200 replaced)
 *   GET    /projects/v1/:id/pull-requests?state=&mine=   the project's pull requests, with whose turn it is for the reader
 *   GET    /projects/v1/:id/pull-requests/:key           one, `key` = <provider>:<repository>:<number> (URL-encoded)
 *   POST   /projects/v1/:id/pull-requests/:key/act       { action, body?, mergeMethod? } as the person, through the source connection
 *   POST   /source/v1/webhooks/:connectionId/github      GitHub App / repository webhook (x-hub-signature-256 with the connection's secret)
 *   POST   /source/v1/webhooks/:connectionId/azure-devops   Azure DevOps service hook (basic auth password = the secret)
 *
 * Every upsert is a `pull-request` event on the project's board stream, with the project's
 * sequence. Words for people: pull request, review, approve, request changes, merge, check;
 * never "PR" in a label, and Azure DevOps's "complete" reads "merge".
 */
import { z } from "zod";
export const PULL_REQUEST_ACT_RPC = "aioe.pullRequest.act";
export const pullRequestProviderSchema = z.enum(["github", "azure-devops"]);
export const pullRequestStateSchema = z.enum(["open", "draft", "merged", "closed"]);
export const reviewerStateSchema = z.enum(["requested", "approved", "changes-requested", "commented"]);
export const checksStateSchema = z.enum(["passing", "failing", "pending", "none"]);
const repositorySchema = z.string().min(1).max(300);
export const pullRequestReviewerSchema = z.object({
    /** The login at the provider; `principal` when it is linked to a person here. */
    login: z.string().min(1).max(200),
    principal: z.string().max(200).optional(),
    state: reviewerStateSchema,
});
export const pullRequestChecksSchema = z.object({
    state: checksStateSchema.default("none"),
    /** The names of the checks that failed. */
    failed: z.array(z.string().max(200)).max(50).default([]),
});
export const pullRequestSchema = z.object({
    provider: pullRequestProviderSchema,
    repository: repositorySchema,
    number: z.number().int().positive(),
    projectId: z.string(),
    title: z.string().max(500),
    url: z.string().max(2000),
    author: z.object({ login: z.string().max(200), principal: z.string().max(200).optional() }),
    state: pullRequestStateSchema,
    branch: z.string().max(300).optional(),
    base: z.string().max(300).optional(),
    reviewers: z.array(pullRequestReviewerSchema).max(50),
    checks: pullRequestChecksSchema,
    conflicts: z.boolean(),
    /** The board task it belongs to (`T-n`), the team, the plan, and the repository's id in the project. */
    task: z.string().max(100).optional(),
    teamId: z.string().max(200).optional(),
    planId: z.string().max(200).optional(),
    rootId: z.string().max(200),
    /** Who last changed it here: the person, and the agent when one made the change. */
    by: z.object({ principal: z.string().max(200).optional(), actor: z.string().max(200).optional() }),
    /** Where the change came from: a workbench's monitor, a provider webhook, or an action taken here. */
    source: z.enum(["workbench", "webhook", "organisation"]),
    updatedAt: z.string(),
    mergedAt: z.string().optional(),
    version: z.number().int().positive(),
    seq: z.number().int().nonnegative(),
});
/** What a monitor sends: the row without what the store assigns. Fields left out keep their stored values. */
export const pullRequestUpsertSchema = z.object({
    provider: pullRequestProviderSchema,
    repository: repositorySchema,
    number: z.number().int().positive(),
    title: z.string().max(500),
    url: z.string().max(2000),
    author: z.object({ login: z.string().max(200), principal: z.string().max(200).optional() }),
    state: pullRequestStateSchema,
    branch: z.string().max(300).optional(),
    base: z.string().max(300).optional(),
    reviewers: z.array(pullRequestReviewerSchema).max(50).optional(),
    checks: pullRequestChecksSchema.optional(),
    conflicts: z.boolean().optional(),
    task: z.string().max(100).optional(),
    teamId: z.string().max(200).optional(),
    planId: z.string().max(200).optional(),
    rootId: z.string().max(200).optional(),
    mergedAt: z.string().optional(),
});
export const PULL_REQUEST_ACTIONS = ["approve", "request-changes", "comment", "merge", "close"];
export const pullRequestActionSchema = z.enum(PULL_REQUEST_ACTIONS);
export const mergeMethodSchema = z.enum(["merge", "squash", "rebase"]);
export const pullRequestActSchema = z.object({
    action: pullRequestActionSchema,
    body: z.string().max(20_000).optional(),
    mergeMethod: mergeMethodSchema.optional(),
});
/** The organisation's rules for merging, per project (the policy's `pullRequests` clause). */
export const pullRequestsPolicySchema = z.object({
    requiredApprovals: z.number().int().min(0).max(10).default(1),
    allowSelfMerge: z.boolean().default(false),
    mergeMethods: z.array(mergeMethodSchema).min(1).default(["merge", "squash", "rebase"]),
    requireChecks: z.boolean().default(true),
    agentsMayMerge: z.boolean().default(false),
});
export const pullRequestKey = (p) => `${p.provider}:${p.repository}:${p.number}`;
export function parsePullRequestKey(key) {
    const m = /^(github|azure-devops):(.+):(\d+)$/.exec(key);
    if (!m)
        return undefined;
    return { provider: m[1], repository: m[2], number: Number(m[3]) };
}
/**
 * Whose turn: yours when your review is asked, or it is yours and changes were requested, a check
 * failed or it conflicts; theirs while it waits on someone else; none once merged or closed.
 */
export function pullRequestTurn(p, me) {
    if (p.state === "merged" || p.state === "closed")
        return "none";
    const mine = (x) => (me.principal !== undefined && x.principal === me.principal) || (me.logins?.includes(x.login) ?? false);
    if (p.reviewers.some((r) => r.state === "requested" && mine(r)))
        return "yours";
    if (mine(p.author) && (p.reviewers.some((r) => r.state === "changes-requested") || p.checks.state === "failing" || p.conflicts))
        return "yours";
    return "theirs";
}
//# sourceMappingURL=pullRequests.js.map